import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError, forkJoin } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Permission, PermissionApiResponse, PermissionDto } from '../models/Permission';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';

export interface PermissionCreate {
  name: string;
  description?: string;
  groupId: number;
  createdBy: string;
}

export interface PermissionUpdate {
  name?: string;
  description?: string;
  groupId?: number;
  updatedBy: string;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  // Usa proxy em desenvolvimento e host do microserviço em produção
  private baseUrl = environment.microservices.profileGroup;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  // ===== CRUD de Permissões =====

  /**
   * Busca todas as permissões
   */
  getPermissions(page: number = 1, pageSize: number = 10): Observable<PermissionApiResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<PermissionApiResponse>(`${this.baseUrl}/GetPermissions`, { params }).pipe(
      map(response => {
        // Garante que sempre retorne uma estrutura válida
        if (!response) {
          return {
            data: [],
            pagination: {
              currentPage: page,
              pageSize: pageSize,
              totalCount: 0,
              totalPages: 0
            }
          };
        }
        // Garante que data seja um array
        if (!response.data || !Array.isArray(response.data)) {
          response.data = [];
        }
        return response;
      }),
      catchError(err => {
        console.error('❌ [PermissionsService] Erro ao buscar permissões:', err);
        this.errorService.showErrorMessage('Erro ao buscar permissões');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca todas as permissões e retorna apenas os dados mapeados
   */
  getAllPermissions(): Observable<Permission[]> {
    return this.getPermissions().pipe(
      map(response => {
        // Validação para evitar erro de undefined
        if (!response || !response.data) {
          return [];
        }
        return response.data.map(dto => this.mapFromPermissionDTO(dto));
      }),
      catchError(err => {
        console.error('❌ [PermissionsService] Erro ao buscar todas as permissões:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca permissões por grupo
   */
  getPermissionsByGroup(groupId: number, page: number = 1, pageSize: number = 10): Observable<PermissionApiResponse> {
    const params = new HttpParams()
      .set('groupId', groupId.toString())
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<PermissionApiResponse>(`${this.baseUrl}/GetPermissions`, { params }).pipe(
      map(response => {
        // Garante que sempre retorne uma estrutura válida
        if (!response) {
          return {
            data: [],
            pagination: {
              currentPage: page,
              pageSize: pageSize,
              totalCount: 0,
              totalPages: 0
            }
          };
        }
        // Garante que data seja um array
        if (!response.data || !Array.isArray(response.data)) {
          response.data = [];
        }
        return response;
      }),
      catchError(err => {
        console.error(`❌ [PermissionsService] Erro ao buscar permissões do grupo ${groupId}:`, err);
        this.errorService.showErrorMessage('Erro ao buscar permissões por grupo');
        // Retorna estrutura vazia em vez de erro
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca todas as permissões por grupo e retorna apenas os dados mapeados
   */
  getAllPermissionsByGroup(groupId: number): Observable<Permission[]> {
    return this.getPermissionsByGroup(groupId).pipe(
      map(response => {
        // Validação para evitar erro de undefined
        if (!response || !response.data) {
          return [];
        }
        return response.data.map(dto => this.mapFromPermissionDTO(dto));
      }),
      catchError(err => {
        console.error(`❌ [PermissionsService] Erro ao buscar permissões do grupo ${groupId}:`, err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca permissão por ID
   */
  getPermissionById(id: number): Observable<Permission> {
    return this.http.get<PermissionDto>(`${this.baseUrl}/GetPermissions/${id}`).pipe(
      map(dto => this.mapFromPermissionDTO(dto)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar permissão');
        return throwError(() => err);
      })
    );
  }

  /**
   * Cria uma nova permissão
   */
  createPermission(permissionData: PermissionCreate): Observable<Permission> {
    return this.http.post<PermissionDto>(`${this.baseUrl}/CreatePermission`, permissionData).pipe(
      map(dto => this.mapFromPermissionDTO(dto)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar permissão');
        return throwError(() => err);
      })
    );
  }

  /**
   * Atualiza uma permissão existente
   */
  updatePermission(id: number, permissionData: PermissionUpdate): Observable<Permission> {
    return this.http.put<PermissionDto>(`${this.baseUrl}/UpdatePermission/${id}`, permissionData).pipe(
      map(dto => this.mapFromPermissionDTO(dto)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar permissão');
        return throwError(() => err);
      })
    );
  }

  associatePermissionsToGroup(groupId: number, permissionIds: number[], updatedBy: string): Observable<{ message: string; count: number }> {
    return this.http.post<{ message: string; count: number }>(`${this.baseUrl}/AssociatePermissionsToGroup`, {
      groupId,
      permissionIds,
      updatedBy
    }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao associar permissões ao grupo');
        return throwError(() => err);
      })
    );
  }

  /**
   * Remove uma permissão (soft delete)
   */
  deletePermission(id: number, deletedBy: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/DeletePermission/${id}`, {
      body: { deletedBy }
    }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao excluir permissão');
        return throwError(() => err);
      })
    );
  }

  // ===== Métodos de Validação =====

  /**
   * Verifica se o nome da permissão está disponível
   */
  checkPermissionNameAvailability(name: string, excludeId?: number): Observable<{ available: boolean }> {
    let params = new HttpParams().set('name', name);
    if (excludeId) {
      params = params.set('excludeId', excludeId.toString());
    }
    
    return this.http.get<{ available: boolean }>(
      `${this.baseUrl}/CheckPermissionNameAvailability`, 
      { params }
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao verificar disponibilidade do nome da permissão');
        return throwError(() => err);
      })
    );
  }

  /**
   * Verifica se a permissão pode ser deletada
   */
  canDeletePermission(id: number): Observable<{ canDelete: boolean; reason?: string }> {
    return this.http.get<{ canDelete: boolean; reason?: string }>(
      `${this.baseUrl}/CanDeletePermission/${id}`
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao verificar se a permissão pode ser excluída');
        return throwError(() => err);
      })
    );
  }

  // ===== Métodos de Busca e Filtro =====

  /**
   * Busca permissões por nome (filtra localmente ou usa endpoint se disponível)
   */
  searchPermissionsByName(searchTerm: string): Observable<Permission[]> {
    return this.getAllPermissions().pipe(
      map(permissions => permissions.filter(permission => 
        permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (permission.description && permission.description.toLowerCase().includes(searchTerm.toLowerCase()))
      ))
    );
  }

  /**
   * Busca permissões por descrição
   */
  searchPermissionsByDescription(searchTerm: string, page: number = 1, pageSize: number = 10): Observable<PermissionApiResponse> {
    const params = new HttpParams()
      .set('search', searchTerm)
      .set('searchField', 'description')
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<PermissionApiResponse>(`${this.baseUrl}/SearchPermissions`, { params }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar permissões por descrição');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca permissões por múltiplos grupos (otimizado)
   * NOTA: O backend não possui endpoint GetPermissionsByGroups.
   * Esta implementação faz múltiplas chamadas para GetPermissions?groupId={id}
   */
  getPermissionsByGroups(groupIds: number[], page: number = 1, pageSize: number = 10): Observable<PermissionApiResponse> {
    // Se não houver grupos, retorna resposta vazia
    if (groupIds.length === 0) {
      return new Observable(observer => {
        observer.next({
          data: [],
          pagination: {
            currentPage: page,
            pageSize: pageSize,
            totalCount: 0,
            totalPages: 0
          }
        });
        observer.complete();
      });
    }

    // Se houver apenas um grupo, usa o endpoint padrão
    if (groupIds.length === 1) {
      return this.getPermissionsByGroup(groupIds[0], page, pageSize);
    }

    // Para múltiplos grupos, faz chamadas paralelas e combina os resultados
    const requests = groupIds.map(groupId => 
      this.getPermissionsByGroup(groupId, 1, 100) // Busca todas as permissões de cada grupo
    );

    return new Observable(observer => {
      // Usa forkJoin para executar todas as requisições em paralelo
      forkJoin(requests).subscribe({
        next: (responses) => {
          // Combina todos os dados
          const allData: PermissionDto[] = [];
          
          // Valida cada resposta antes de processar
          responses.forEach(response => {
            if (response && response.data && Array.isArray(response.data)) {
              allData.push(...response.data);
            }
          });

          // Remove duplicatas (se houver)
          const uniqueData = allData.filter((item, index, self) =>
            index === self.findIndex(t => t.Id === item.Id)
          );

          // Aplica paginação nos resultados combinados
          const startIndex = (page - 1) * pageSize;
          const endIndex = startIndex + pageSize;
          const paginatedData = uniqueData.slice(startIndex, endIndex);

          observer.next({
            data: paginatedData,
            pagination: {
              currentPage: page,
              pageSize: pageSize,
              totalCount: uniqueData.length,
              totalPages: Math.ceil(uniqueData.length / pageSize)
            }
          });
          observer.complete();
        },
        error: (err) => {
          console.error('❌ [PermissionsService] Erro ao buscar permissões por grupos:', err);
          this.errorService.showErrorMessage('Erro ao buscar permissões por grupos');
          
          // Retorna resposta vazia em caso de erro
          observer.next({
            data: [],
            pagination: {
              currentPage: page,
              pageSize: pageSize,
              totalCount: 0,
              totalPages: 0
            }
          });
          observer.complete();
        }
      });
    });
  }

  /**
   * Busca todas as permissões de múltiplos grupos (retorna apenas dados mapeados)
   */
  getAllPermissionsByGroups(groupIds: number[]): Observable<Permission[]> {
    return this.getPermissionsByGroups(groupIds, 1, 1000).pipe(
      map(response => {
        // Validação para evitar erro de undefined
        if (!response || !response.data) {
          return [];
        }
        return response.data.map(dto => this.mapFromPermissionDTO(dto));
      }),
      catchError(err => {
        console.error('❌ [PermissionsService] Erro ao buscar permissões por grupos:', err);
        return throwError(() => err);
      })
    );
  }

  // ===== Métodos de Auditoria =====

  /**
   * Busca histórico de alterações de uma permissão
   */
  getPermissionHistory(id: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/GetPermissionHistory/${id}`).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar histórico da permissão');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca permissões criadas por um usuário
   */
  getPermissionsByCreator(createdBy: string, page: number = 1, pageSize: number = 10): Observable<PermissionApiResponse> {
    const params = new HttpParams()
      .set('createdBy', createdBy)
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<PermissionApiResponse>(`${this.baseUrl}/GetPermissionsByCreator`, { params }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar permissões por criador');
        return throwError(() => err);
      })
    );
  }

  // ===== Mapeamento de Dados =====

  /**
   * Mapeia dados recebidos da API para o modelo Permission
   */
  private mapFromPermissionDTO(dto: PermissionDto): Permission {
    return {
      id: dto.Id,
      name: dto.Name,
      description: dto.Description,
      groupId: dto.GroupId,
      createdAt: new Date(dto.CreatedAt),
      updatedAt: new Date(dto.UpdatedAt),
      createdBy: dto.CreatedBy,
      updatedBy: dto.UpdatedBy
    };
  }

  /**
   * Mapeia dados do modelo Permission para envio à API
   */
  private mapToPermissionDTO(payload: any): any {
    return {
      name: payload.name,
      description: payload.description || '',
      groupId: payload.groupId,
      createdBy: payload.createdBy,
      updatedBy: payload.updatedBy
    };
  }
}
